import api from './api';

// Types
export interface Group {
  id: number;
  name: string;
  description: string;
  group_type: 'manual' | 'auto' | 'template';
  auto_criteria: Record<string, any>;
  created_at: string;
  updated_at: string;
  created_by: number;
  created_by_name: string;
  is_active: boolean;
  member_count: number;
  last_campaign_sent: string | null;
  members?: GroupMember[];
}

export interface GroupMember {
  id: number;
  reservation: number;
  guest_name: string;
  property_name: string;
  arrival_date: string;
  departure_date: string;
  email: string;
  mobile: string;
  reservation_status: string;
  added_at: string;
}

export interface Campaign {
  id: number;
  name: string;
  description: string;
  campaign_type: 'email' | 'whatsapp' | 'both';
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed' | 'cancelled';
  group: number;
  group_name: string;
  email_template: number | null;
  email_template_name: string | null;
  whatsapp_template: number | null;
  whatsapp_template_name: string | null;
  scheduled_at: string | null;
  sent_at: string | null;
  total_recipients: number;
  successful_sends: number;
  failed_sends: number;
  created_at: string;
  updated_at: string;
  created_by: number;
  created_by_name: string;
  logs?: CampaignLog[];
}

export interface CampaignLog {
  id: number;
  campaign: number;
  reservation: number;
  guest_name: string;
  message_type: 'email' | 'whatsapp';
  recipient: string;
  status: 'pending' | 'sent' | 'failed' | 'bounced' | 'delivered';
  error_message: string | null;
  sent_at: string;
}

export interface GroupTemplate {
  id: number;
  name: string;
  description: string;
  criteria: Record<string, any>;
  created_at: string;
  created_by: number;
  created_by_name: string;
  is_active: boolean;
}

export interface GroupCriteriaOptions {
  properties: Array<{ id: number; name: string; property_id: string }>;
  agents: Array<{ id: number; name: string }>;
  statuses: string[];
  countries: string[];
  states: string[];
  cities: string[];
  market_segments: string[];
  booking_types: string[];
}

export interface GroupAnalytics {
  total_members: number;
  property_breakdown: Record<string, number>;
  status_breakdown: Record<string, number>;
  location_breakdown: Record<string, number>;
  date_range: {
    earliest_arrival: string | null;
    latest_departure: string | null;
  };
  campaigns_sent: number;
  last_campaign_date: string | null;
}

// API Service
export const groupService = {
  // Groups
  getGroups: async (params?: Record<string, string>) => {
    const response = await api.get('/groups/', { params });
    return response.data;
  },

  getGroup: async (id: number) => {
    const response = await api.get(`/groups/${id}/`);
    return response.data;
  },

  createGroup: async (groupData: Partial<Group>) => {
    const response = await api.post('/groups/', groupData);
    return response.data;
  },

  updateGroup: async (id: number, groupData: Partial<Group>) => {
    const response = await api.put(`/groups/${id}/`, groupData);
    return response.data;
  },

  deleteGroup: async (id: number) => {
    const response = await api.delete(`/groups/${id}/`);
    return response.data;
  },

  autoPopulateGroup: async (id: number) => {
    const response = await api.post(`/groups/${id}/auto_populate/`);
    return response.data;
  },

  addMembersToGroup: async (id: number, reservationIds: number[]) => {
    const response = await api.post(`/groups/${id}/add_members/`, {
      reservation_ids: reservationIds
    });
    return response.data;
  },

  removeMemberFromGroup: async (id: number, memberId: number) => {
    const response = await api.delete(`/groups/${id}/remove_member/?member_id=${memberId}`);
    return response.data;
  },

  getGroupAnalytics: async (id: number) => {
    const response = await api.get(`/groups/${id}/analytics/`);
    return response.data;
  },

  // Campaigns
  getCampaigns: async (params?: Record<string, string>) => {
    const response = await api.get('/campaigns/', { params });
    return response.data;
  },

  getCampaign: async (id: number) => {
    const response = await api.get(`/campaigns/${id}/`);
    return response.data;
  },

  createCampaign: async (campaignData: Partial<Campaign>) => {
    const response = await api.post('/campaigns/', campaignData);
    return response.data;
  },

  updateCampaign: async (id: number, campaignData: Partial<Campaign>) => {
    const response = await api.put(`/campaigns/${id}/`, campaignData);
    return response.data;
  },

  deleteCampaign: async (id: number) => {
    const response = await api.delete(`/campaigns/${id}/`);
    return response.data;
  },

  sendCampaign: async (id: number, sendData: { send_immediately: boolean; scheduled_datetime?: string }) => {
    const response = await api.post(`/campaigns/${id}/send_campaign/`, sendData);
    return response.data;
  },

  // Group Templates
  getGroupTemplates: async (params?: Record<string, string>) => {
    const response = await api.get('/group-templates/', { params });
    return response.data;
  },

  getGroupTemplate: async (id: number) => {
    const response = await api.get(`/group-templates/${id}/`);
    return response.data;
  },

  createGroupTemplate: async (templateData: Partial<GroupTemplate>) => {
    const response = await api.post('/group-templates/', templateData);
    return response.data;
  },

  updateGroupTemplate: async (id: number, templateData: Partial<GroupTemplate>) => {
    const response = await api.put(`/group-templates/${id}/`, templateData);
    return response.data;
  },

  deleteGroupTemplate: async (id: number) => {
    const response = await api.delete(`/group-templates/${id}/`);
    return response.data;
  },

  createGroupFromTemplate: async (id: number, groupData: { name: string; description?: string }) => {
    const response = await api.post(`/group-templates/${id}/create_group/`, groupData);
    return response.data;
  },

  // Criteria Options
  getGroupCriteriaOptions: async () => {
    const response = await api.get('/api/group-criteria-options/');
    return response.data;
  },

  /** Audience count for campaigns (unique emails or phones). */
  previewAudience: async (criteria: Record<string, unknown>) => {
    const response = await api.post('/groups/preview_audience/', criteria);
    return response.data;
  },
};

export default groupService; 